// First Created - 04 Aug 2026
// Author - Atul Pandey
// Purpose - API endpoints for evaluation reporting

import { apiSlice } from '@/app/api/apiSlice'

export const evaluationReportsApiSlice =
    apiSlice.injectEndpoints({
        endpoints: (builder) => ({
            getIndividualChallengeEvaluationReport:
                builder.query({
                    query: ({
                        orgId,
                        trailId,
                        sessionId,
                        challengeId,
                        userId,
                        evaluationNumber,
                    }) => ({
                        url:
                            '/reports/evaluations/individual',

                        params: {
                            orgId,
                            trailId,
                            sessionId,
                            challengeId,
                            userId,
                            evaluationNumber,
                        },
                    }),

                    /*
                    |--------------------------------------------------------------------------
                    | Keep report data fresh
                    |--------------------------------------------------------------------------
                    |
                    | Reports are generated from live evaluation records.
                    | We do not normalize these into the Redux entity adapter
                    | because each response represents one specific report scope.
                    |
                    */

                    keepUnusedDataFor: 60,
                }),
            downloadIndividualChallengeEvaluationPowerPoint:
                builder.mutation({
                    query: ({
                        orgId,
                        trailId,
                        sessionId,
                        challengeId,
                        userId,
                        evaluationNumber,
                    }) => ({
                        url:
                            '/reports/evaluations/individual/powerpoint',

                        method: 'GET',

                        params: {
                            orgId,
                            trailId,
                            sessionId,
                            challengeId,
                            userId,
                            evaluationNumber,
                        },

                        responseHandler:
                            async (response) =>
                                response.blob(),

                        cache: 'no-cache',
                    }),
                }),
            // 08 Aug 2026
            // Generate and download one evaluation snapshot as PDF.
            downloadIndividualChallengeEvaluationPdf:
                builder.mutation({
                    query: ({
                        orgId,
                        trailId,
                        sessionId,
                        challengeId,
                        userId,
                        evaluationNumber,
                    }) => ({
                        url:
                            '/reports/evaluations/individual/pdf',

                        method:
                            'GET',

                        params: {
                            orgId,
                            trailId,
                            sessionId,
                            challengeId,
                            userId,
                            evaluationNumber,
                        },

                        responseHandler:
                            async (
                                response
                            ) => {
                                if (
                                    !response.ok
                                ) {
                                    const errorData =
                                        await response
                                            .json()
                                            .catch(
                                                () => ({
                                                    message:
                                                        'The evaluation PDF could not be generated.',
                                                })
                                            )

                                    throw errorData
                                }

                                return response.blob()
                            },

                        cache:
                            'no-cache',
                    }),
                }),    
            // 07 Aug 2026
            // Generate and download the complete challenge evaluation journey PowerPoint.
            downloadMultiChallengeEvaluationPowerPoint:
                builder.mutation({
                    query: ({
                        orgId,
                        trailId,
                        sessionId,
                        challengeId,
                        userId,
                    }) => ({
                        url:
                            '/reports/evaluations/challenge-evaluations/multi/powerpoint',

                        method:
                            'GET',

                        params: {
                            orgId,
                            trailId,
                            sessionId,
                            challengeId,
                            userId,
                        },

                        responseHandler:
                            async (
                                response
                            ) => {
                                if (
                                    !response.ok
                                ) {
                                    const errorData =
                                        await response
                                            .json()
                                            .catch(
                                                () => ({
                                                    message:
                                                        'The multi evaluation PowerPoint could not be generated.',
                                                })
                                            )

                                    throw errorData
                                }

                                return response.blob()
                            },

                        cache:
                            'no-cache',
                    }),
                }),
            // 08 Aug 2026
            // Generate and download the complete challenge evaluation journey as PDF.
            downloadMultiChallengeEvaluationPdf:
                builder.mutation({
                    query: ({
                        orgId,
                        trailId,
                        sessionId,
                        challengeId,
                        userId,
                    }) => ({
                        url:
                            '/reports/evaluations/challenge-evaluations/multi/pdf',

                        method:
                            'GET',

                        params: {
                            orgId,
                            trailId,
                            sessionId,
                            challengeId,
                            userId,
                        },

                        responseHandler:
                            async (
                                response
                            ) => {
                                if (
                                    !response.ok
                                ) {
                                    const errorData =
                                        await response
                                            .json()
                                            .catch(
                                                () => ({
                                                    message:
                                                        'The evaluation journey PDF could not be generated.',
                                                })
                                            )

                                    throw errorData
                                }

                                return response.blob()
                            },

                        cache:
                            'no-cache',
                    }),
                }),
            // 08 Aug 2026
            // Generate and email one evaluation snapshot as PDF.
            // emailIndividualChallengeEvaluationReport:
            emailIndividualChallengeEvaluationPdf:
                builder.mutation({
                    query: ({
                        orgId,
                        trailId,
                        sessionId,
                        challengeId,
                        userId,
                        evaluationNumber,
                    }) => ({
                        url:
                            // '/reports/evaluations/individual/email',
                            '/reports/evaluations/individual/email/pdf',

                        method:
                            'POST',

                        body: {
                            orgId,
                            trailId,
                            sessionId,
                            challengeId,
                            userId,
                            evaluationNumber,
                        },
                    }),
                }),
            // 08 Aug 2026
            // Generate and email the complete evaluation journey as PDF.
            // emailMultiChallengeEvaluationReport:
            emailMultiChallengeEvaluationPdf:
                builder.mutation({
                    query: ({
                        orgId,
                        trailId,
                        sessionId,
                        challengeId,
                        userId,
                    }) => ({
                        url:
                            // '/reports/evaluations/challenge-evaluations/multi/email',
                            '/reports/evaluations/challenge-evaluations/multi/email/pdf',

                        method:
                            'POST',

                        body: {
                            orgId,
                            trailId,
                            sessionId,
                            challengeId,
                            userId,
                        },
                    }),
                }),
            // 08 Aug 2026
            // Generate and email one evaluation snapshot as an inline HTML report.
            emailIndividualChallengeEvaluationInline:
                builder.mutation({
                    query: ({
                        orgId,
                        trailId,
                        sessionId,
                        challengeId,
                        userId,
                        evaluationNumber,
                    }) => ({
                        url:
                            '/reports/evaluations/individual/email/inline',

                        method:
                            'POST',

                        body: {
                            orgId,
                            trailId,
                            sessionId,
                            challengeId,
                            userId,
                            evaluationNumber,
                        },
                    }),
                }), 
            // 08 Aug 2026
            // Generate and email the complete evaluation journey
            // as an inline HTML report.
            emailMultiChallengeEvaluationInline:
                builder.mutation({
                    query: ({
                        orgId,
                        trailId,
                        sessionId,
                        challengeId,
                        userId,
                    }) => ({
                        url:
                            '/reports/evaluations/challenge-evaluations/multi/email/inline',

                        method:
                            'POST',

                        body: {
                            orgId,
                            trailId,
                            sessionId,
                            challengeId,
                            userId,
                        },
                    }),
                }),   
        }),

        overrideExisting: false,
    })

export const {
    useGetIndividualChallengeEvaluationReportQuery,
    useLazyGetIndividualChallengeEvaluationReportQuery,
    useDownloadIndividualChallengeEvaluationPowerPointMutation,
    // 08 Aug 2026
    useDownloadIndividualChallengeEvaluationPdfMutation,
    // 07 Aug 2026
    useDownloadMultiChallengeEvaluationPowerPointMutation,
     // 08 Aug 2026
    useDownloadMultiChallengeEvaluationPdfMutation,
    useEmailIndividualChallengeEvaluationPdfMutation,
    useEmailMultiChallengeEvaluationPdfMutation,
    
    useEmailIndividualChallengeEvaluationInlineMutation,
    useEmailMultiChallengeEvaluationInlineMutation,
} = evaluationReportsApiSlice